import IconSearch from '@picsart/icons/IconSearch';
import cls from 'classnames';
import { useState, useEffect } from 'react';
import { matchSorter } from 'match-sorter';

import { getQueryParamValue } from '../../../utils';
import { ALL_TOOLS } from '../../../data/tools';

import { ISingleToolSearchProps } from './types';
import Results from './Results';
import NoResults from './NoResults';
import DefaultResults from './DefaultResults';
import useStyles from './styles';

const SEARCH_TYPING_TIMEOUT = 300;
let timeout: NodeJS.Timeout;

function SingleToolSearch({
  className,
  collabsable,
  analytics,
  toolName,
  pageName,
}: ISingleToolSearchProps) {
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [results, setResults] = useState([]);
  const [isInputFocused, setInputFocused] = useState(false);
  const [noResultsVisible, setNoResultsVisible] = useState(false);
  const [defaultResultsVisible, setDefaultResultsVisible] = useState(false);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;
    clearTimeout(timeout);
    setSearchText(value);
    setIsTyping(true);
    timeout = setTimeout(() => {
      setIsTyping(false);
      analytics && analytics.searchBarType(pageName, toolName);
    }, SEARCH_TYPING_TIMEOUT);
  };

  const onFocus = () => {
    setInputFocused(true);
  };
  const onBlur = () => {
    setInputFocused(false);
  };

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInputFocused(true);
    analytics && analytics.searchBarClick(pageName, toolName);
  };

  useEffect(() => {
    if (!isInputFocused) {
      return;
    }

    if (!searchText) {
      setNoResultsVisible(false);
      setResults([]);
      setDefaultResultsVisible(true);
      return;
    }

    if (!isTyping && searchText) {
      const found = matchSorter(ALL_TOOLS, searchText.trim(), {
        keys: ['name', 'categories'],
        threshold: matchSorter.rankings.CONTAINS,
      });

      setDefaultResultsVisible(false);

      if (found.length) {
        setNoResultsVisible(false);
        setResults(found);
      } else {
        setNoResultsVisible(true);
        setResults([]);
      }
    }
  }, [isTyping, searchText, isInputFocused]);

  useEffect(() => {
    const closeResultsDropdown = () => {
      setNoResultsVisible(false);
      setResults([]);
      setDefaultResultsVisible(false);
      setInputFocused(false);
    };
    window.addEventListener('click', closeResultsDropdown);

    return () => {
      window.removeEventListener('click', closeResultsDropdown);
    };
  }, []);

  useEffect(() => {
    const query = getQueryParamValue('search');

    if (query) {
      setSearchText(query);
    }
  }, []);

  return (
    <div className={cls(classes.root, className)}>
      <label className={classes.search}>
        <IconSearch className={cls(classes.icon, collabsable && classes.collasableInputIcon)} />
        <input
          autoFocus={false}
          className={cls(classes.input, collabsable && classes.collapsableInput)}
          value={searchText}
          onChange={onChange}
          placeholder="Search"
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onClick}
        />
      </label>
      {Boolean(results.length) && (
        <Results
          analytics={analytics}
          items={results}
          searchText={searchText}
          toolName={toolName}
          pageName={pageName}
        />
      )}
      {noResultsVisible && (
        <NoResults analytics={analytics} toolName={toolName} pageName={pageName} />
      )}
      {defaultResultsVisible && (
        <DefaultResults analytics={analytics} toolName={toolName} pageName={pageName} />
      )}
    </div>
  );
}

export default SingleToolSearch;
